import React from "react";
import { Link } from "react-router-dom";
import { API, mediaUrl } from "@/lib/api";
import OrderTimeline from "@/components/OrderTimeline";
import { Button } from "@/components/ui/button";
import { ExternalLink, Printer } from "lucide-react";

const money = (n) => `₹${Number(n || 0).toFixed(2)}`;
const Row = ({ label, value, testid }) => (
  value ? (
    <div className="flex justify-between gap-4 text-xs">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-mono text-right break-all" data-testid={testid}>{value}</span>
    </div>
  ) : null
);

/** Everything the clinic needs about one order, without leaving the list. */
export const OrderRowDetails = ({ order }) => (
  <div className="mt-4 pt-4 border-t border-border grid lg:grid-cols-3 gap-6" data-testid={`adm-order-details-${order.id}`}>
    <div className="space-y-3">
      <div className="overline">Items</div>
      {order.items?.map((it) => (
        <div key={it.product_id} className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-muted overflow-hidden shrink-0">
            {it.image && <img src={mediaUrl(it.image)} alt={it.name} className="w-full h-full object-cover" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm truncate">{it.name}</div>
            <div className="text-xs text-muted-foreground">{money(it.price)} × {it.quantity}</div>
          </div>
          <div className="text-sm">{money(it.line_total)}</div>
        </div>
      ))}
      <div className="pt-3 border-t border-border space-y-1.5">
        <Row label="Subtotal" value={money(order.gross_subtotal ?? order.subtotal)} />
        {order.discount_amount > 0 && (
          <Row label={`Discount ${order.discount_code || ""}`} value={`− ${money(order.discount_amount)}`} testid={`adm-order-discount-${order.id}`} />
        )}
        <Row label={`GST @ ${Math.round((order.tax_rate || 0) * 100)}%`} value={money(order.tax_amount)} />
        <Row label="Total" value={money(order.total)} />
      </div>
    </div>

    <div className="space-y-4">
      <div>
        <div className="overline mb-1.5">Ship to</div>
        <div className="text-sm">{order.user_name}</div>
        <div className="text-xs text-muted-foreground whitespace-pre-line" data-testid={`adm-order-address-${order.id}`}>{order.address}</div>
        <div className="text-xs text-muted-foreground">{order.user_phone} · {order.user_email}</div>
      </div>
      <div className="space-y-1.5">
        <div className="overline mb-1.5">Payment</div>
        <Row label="Razorpay order" value={order.razorpay_order_id} testid={`adm-order-rzp-order-${order.id}`} />
        <Row label="Payment id" value={order.razorpay_payment_id} testid={`adm-order-payment-id-${order.id}`} />
        <Row label="Confirmed via" value={order.confirmed_via} />
        <Row label="Paid at" value={order.paid_at ? new Date(order.paid_at).toLocaleString() : ""} />
        <Row label="Invoice" value={order.invoice_number} />
      </div>
      <div className="space-y-1.5">
        <div className="overline mb-1.5">Courier</div>
        <Row label="Shiprocket order" value={order.shiprocket_order_id} testid={`adm-order-courier-${order.id}`} />
        <Row label="AWB" value={order.awb_code} />
        <Row label="Courier" value={order.courier_name} />
        <Row label="Tracking" value={order.tracking_status} />
        {order.shiprocket_error && (
          <p className="text-xs text-destructive" data-testid={`adm-order-courier-error-${order.id}`}>{order.shiprocket_error}</p>
        )}
        {!order.shiprocket_order_id && !order.awb_code && (
          <p className="text-xs text-muted-foreground">Not handed to a courier yet.</p>
        )}
      </div>
    </div>

    <div className="space-y-4">
      <div className="overline">Journey</div>
      <OrderTimeline status={order.status} history={order.status_history || []} />
      <div className="flex flex-wrap gap-2 pt-2">
        <Button variant="outline" size="sm" className="btn-pill" onClick={() => window.open(`${API}/admin/shipping/orders/${order.id}/label?format=detailed`, "_blank")} data-testid={`adm-order-label-${order.id}`}>
          <Printer size={13} className="mr-1.5" /> Label
        </Button>
        <Button variant="outline" size="sm" className="btn-pill" onClick={() => window.open(`${API}/admin/shipping/orders/${order.id}/label?format=sticker`, "_blank")} data-testid={`adm-order-sticker-${order.id}`}>
          <Printer size={13} className="mr-1.5" /> Sticker
        </Button>
        <Link to={`/orders/${order.id}`}>
          <Button variant="ghost" size="sm" className="btn-pill" data-testid={`adm-order-open-${order.id}`}>
            <ExternalLink size={13} className="mr-1.5" /> Buyer view
          </Button>
        </Link>
      </div>
    </div>
  </div>
);

export default OrderRowDetails;
